import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { Modal, IconButton, Button, Typography, Tooltip, Box, Popper, Fade, Paper, List, ListItem, ListItemText, Popover, Pagination } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import CustomAccordianV2 from "../../components/dms_common/CustomAccordianV2";
import CustomGenericModal from "../../components/dms_common/CustomGenericModal";
import FormHeader from "../../components/dms_common/FormHeader";
import ScreenOverlay from "../../components/dms_common/ScreenOverlay";
import SearchV2 from "../../components/dms_common/SearchV2";
import { customStyle, GenericModal, getGenericModalProps, initialGenericModalProps } from "../viewOrder/ViewOrderUtils";
import {  EditPrimaryReturnConfig, EditPrimaryReturnExpandedRowConfig, EditPrimaryReturnMetadata, ViewPrimaryReturnMetadata } from "./ViewPrimaryReturnConfig";
import "./EditPrimaryReturn.css";
import BorderHeading from "../../components/heading/BorderHeading";
import { Loader } from "../../components/loader/Loader";
import CustomTablewithTotalInfoV2 from "../../components/dms_common/CustomTablewithTotalInfoV2";
import { getInvoiceDetails, raiseReturn } from "../../services/manageOrderService";
import { calculateReturnAmount, getBatchDetailsFromDb, getBatchDetailsFromDbPrimary } from "../SalesReturn/SalesReturnService";
import { BatchDetails, generateReturnRows, generateSummaryValuesForEdit, getPayload, SkuData } from "./PrimaryReturnUtils";
import "../primaryOrder/PrimaryReturn.css"

import { filterData, searchWithStatusFilter } from "../../components/dms_common/DmsCommonServices";
import PrimaryReturnSelect from "./PrimaryReturnSelect";
import { getLabel } from "./PrimaryReturnEdit";
import { getConfigData } from "../../services/configurationService";
const EditPrimaryReturn = () => {

  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  const navigate = useNavigate();
  const location = useLocation();
  const {caseUnitLabelPrimaryReturn, packUnitLabelPrimaryReturn, otherUnitLabelPrimaryReturn} = ConfigDictionary;

  const invoiceNumber = location?.state?.invoiceNumber;
  const grnNumber = location?.state?.grnNumber;

  const [genericModalProps, setGenericModalProps] = useState<GenericModal>(
    initialGenericModalProps
  );
  const [searchedValue, setSearchedValue] = useState<string>("");
  const [allowedReturnQtyTypes, setAllowedReturnQtyTypes] = useState<string[]>(["ACCEPTED", "REJECTED"]);

  const [loader, setLoader] = useState(false);

  const [rows, setRows] = useState<SkuData[]>([]);
  const [filteredRows, setFilteredRows] = useState<SkuData[]>([]);

  const [allowTableScroll, setAllowTableScroll] = useState<boolean>(false);
  const [scrollTop, setScrollTop] = useState<number>(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isScrolling, setIsScrolling] = useState(false);

  const [invoiceData, setInvoiceData] = useState<any>();

  const [approvalButtonLoading, setApprovalButtonLoading] = useState<boolean>(false);
  const approvalButtonLoadingRef = useRef<boolean>(false);

  const [selectedQtyType, setSelectedQtyType] = useState<string>("ACCEPTED");

  const [hasAnyReturnedQuantity, setHasAnyReturnedQuantity] = useState<boolean>(false);

  const handleChangeQtyType = (type: string) => {
    setSelectedQtyType(type);
  }

  const handleSearchValueChange = (newValue: string) => {
    setSearchedValue(newValue);
  };

  const redirectBack = useRef<boolean>(false);

  useEffect(() => {
    setHasAnyReturnedQuantity(
      rows.some((sku) =>
        sku.batches.some((batch) =>
          Object.values(batch.qtyReasonMap || {}).some((qtyReasons: any) =>
            qtyReasons.some(
              (qtyReason: any) =>
                (qtyReason.caseQty && qtyReason.caseQty > 0) ||
                (qtyReason.pieceQty && qtyReason.pieceQty > 0) ||
                (qtyReason.otherQty && qtyReason.otherQty > 0)
            )
          )
        )
      )
    )
  }, [rows]);

  const handleClickRaiseButton = () => {
    if (!hasAnyReturnedQuantity) {
      setGenericModalProps(
        getGenericModalProps(
          "error",
          "Add quantity for atleast one sku to return!",
        )
      );
      return;
    }
    setGenericModalProps(
      getGenericModalProps(
        "positive",
        "Are you sure you want to raise this primary return?",
        "Proceed",
        "Cancel",
        async () => {
          if (!approvalButtonLoading){
            await handleClickRaiseForApproval()
          }
        },
        () => {setGenericModalProps(initialGenericModalProps)}
      )
    )
  }
  
  const handleClickRaiseForApproval = async () => {
    if (approvalButtonLoadingRef.current) return
    const payload = getPayload(rows, invoiceData);
    setApprovalButtonLoading(true);
    approvalButtonLoadingRef.current = true
    try {
      const raiseResponse = await raiseReturn(payload);
      redirectBack.current = true;
      setGenericModalProps(
        getGenericModalProps(
          "success",
          `Return raised successfully with Return Number: ${raiseResponse.invoiceNumber}`,
          "OK",
          "",
          () => {navigate(-1);},
          () => {navigate(-1)},
        )
      )
    } catch (error) {
      console.log("Error while raising return:", error);
      setGenericModalProps(getGenericModalProps(
        "error",
        "Failed to raise return. Please try again.",
      ))
    } finally{
      setApprovalButtonLoading(false);
      approvalButtonLoadingRef.current = false
    }
  };

  const fetchRequiredConfigs = async () => {
    const allowedReturnsConfig = await getConfigData("dms_configuration", "allowedReturnQtyTypes");
    const allowedReturns = allowedReturnsConfig[0]?.configValue ?? ["REJECTED"];
    setAllowedReturnQtyTypes(allowedReturns);
    setSelectedQtyType(allowedReturns[0] || "REJECTED");
  }

  const fetchInvoiceData = async () => {
    setLoader(true)
    try{
      const data = await getInvoiceDetails(invoiceNumber);
      const mainInvoice = data.filter((item: any) => (item.type.toLowerCase() === "primary" || item.type.toLowerCase() === "invoice"))[0];

      if (!mainInvoice){
        setGenericModalProps(getGenericModalProps(
          "error",
          `No primary invoice found with invoice number ${invoiceNumber}.`,
        ))
        throw new Error("No primary invoice found");
      }

      mainInvoice.currentDate = new Date().toISOString().split("T")[0];
      
      const summaryValues = generateSummaryValuesForEdit(rows);
      mainInvoice.totalReturnValue = summaryValues.totalReturnedValue;
      mainInvoice.totalReturnedCaseQuantity = summaryValues.totalReturnedCases;
      mainInvoice.totalReturnedPieceQuantity = summaryValues.totalReturnedPieces;
      mainInvoice.totalReturnedOtherQuantity = summaryValues.totalReturnedOtherUnits;

      setInvoiceData(mainInvoice);
      const rowData = await generateReturnRows(data, grnNumber);
      setRows(rowData);
    } catch(error){
      console.log(error);
      setGenericModalProps(getGenericModalProps(
        "error",
        "Something went wrong"
      ))
    } finally {
      setLoader(false)
    }
  }

  useEffect(() => {

      const summaryValues = generateSummaryValuesForEdit(rows);
      setInvoiceData((prevData: any) => {
        if (!prevData) return prevData;
        return {
          ...prevData,
          totalReturnValue: summaryValues.totalReturnedValue,
          totalReturnedCaseQuantity: summaryValues.totalReturnedCases,
          totalReturnedPieceQuantity: summaryValues.totalReturnedPieces,
          totalReturnedOtherQuantity: summaryValues.totalReturnedOtherUnits,
        };
      });

  }, [rows])

  useEffect(() => {
    fetchInvoiceData()
    fetchRequiredConfigs()
    handleClosePopover();
  }, [invoiceNumber])

  useEffect(() => {
    let filtered = filterData([...rows], searchedValue?.toLowerCase(), searchWithStatusFilter)
  
      setFilteredRows(filtered);
  }, [rows, searchedValue, searchWithStatusFilter, invoiceData]);



  const handleBatchIdEditClick = async (rowData: SkuData) => {
    const batchDetails = await getBatchDetailsFromDbPrimary("primaryReturn");
    handleClosePopover()
    navigate("edit", {state:{
      rowData:rowData,
      batchDetails:(batchDetails && batchDetails[rowData.batchCode]) ?? {}
    }});
  }

 
  const [activeRow, setActiveRow] = useState<BatchDetails>({} as BatchDetails);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClickInfoSku = (event: React.MouseEvent<HTMLElement>, rowData: SkuData) => {
    setActiveRow(rowData.batches[0]);
    setAnchorEl(event.currentTarget);
    setPopoverOpen(true)
    setSelectedQtyType(allowedReturnQtyTypes[0]);
  }

  const handleClosePopover = () => {
    setAnchorEl(null);
    setActiveRow({} as BatchDetails);
    setPopoverOpen(false)
  }

  const handleClickInfoBatch = (event: React.MouseEvent<HTMLElement>, batchData: BatchDetails) => {
    setActiveRow(batchData);
    setPopoverOpen(true)
    setAnchorEl(event.currentTarget);
    setSelectedQtyType(allowedReturnQtyTypes[0]);
  }


  const [popoverOpen, setPopoverOpen] = useState(false)

  const columns: any[] = EditPrimaryReturnConfig(handleBatchIdEditClick, handleClickInfoSku).columns;
  const expandedRowColumns: any[] = EditPrimaryReturnExpandedRowConfig(handleClickInfoBatch).columns;

  return (
    <div className="view-primary-order-container">
      <CustomGenericModal
        type={genericModalProps?.type}
        message={genericModalProps?.message}
        leftButtonText={genericModalProps?.leftButtonText}
        rightButtonText={genericModalProps?.rightButtonText}
        modalOpen={genericModalProps?.open}
        onLeftButtonClick={genericModalProps?.onLeftButtonClick}
        onRightButtonClick={genericModalProps?.onRightButtonClick}
        leftButtonPadding={genericModalProps?.leftButtonPadding}
        onClose={() =>{
            if (redirectBack.current){
              navigate(-1);
            }
            setGenericModalProps(initialGenericModalProps)
          }
        } 
        width={genericModalProps?.width}
      />
      <ScreenOverlay apply={false} />

      <Popover
        open={popoverOpen}
        anchorEl={anchorEl}
        onClose={() => setPopoverOpen(false)} // replace with your close handler if different
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        TransitionComponent={Fade}
        transitionDuration={150}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            padding: "12px",
            boxShadow: "0px 2px 12px rgba(0,0,0,0.15)",
            minWidth: 330,
            maxWidth: 420,
            zIndex: 99999,
            // Use transform to mimic the small offset you had with Popper offset: [0,8]
            transform: "translateY(8px)",
          },
        }}
      >
        {/* Popover provides the Paper already via PaperProps; use the same inner content */}
        {activeRow && (
          <Box>
            <div className="qty-type" style={{ margin: 0, marginBottom: 8 }}>
              {allowedReturnQtyTypes.map((key: string) => (
                <PrimaryReturnSelect
                  key={key}
                  label={getLabel(key)}
                  isSelected={selectedQtyType === key}
                  onSelect={() => handleChangeQtyType(key)}
                />
              ))}
            </div>

            {/* Rows */}
            <Box sx={{ overflow: "auto", px: 0, flex: "1 1 auto", maxHeight: `150px`, scrollbarWidth: "none" }}>
              <List disablePadding sx={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                {(() => {
                  // normalize source entries
                  const rows = (activeRow.qtyReasonMap && activeRow.qtyReasonMap[selectedQtyType]) || [];
                  const filteredRows = rows.filter(
                    (row: any) => row.caseQty > 0 || row.pieceQty > 0 || row.otherQty > 0
                  );
                  if (filteredRows.length === 0) {
                    return (
                      <ListItem sx={{ py: 2, px: 2 }}>
                        <ListItemText
                          primary={
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.72rem" }}>
                              No returns quantity entered
                            </Typography>
                          }
                        />
                      </ListItem>
                    );
                  }

                  return filteredRows.map((row: any, i: number) => {
                    if (!(row.caseQty > 0 || row.pieceQty > 0 || row.otherQty > 0)) return <></>
                    return (
                      <Box
                        key={i}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          py: 1,
                          borderBottom: i !== filteredRows.length - 1 ? "1px dashed #eee" : "none",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "8px",
                            width: "min-content",
                            border: "1px solid #EEEEEE",
                            borderRadius: "4px",
                            padding: "8px",
                          }}
                        >
                          {caseUnitLabelPrimaryReturn && (
                            <>
                              <div style={{ display: "flex", alignItems: "center", gap: "2px", color: "rgb(124, 124, 124)" }}>
                                <span style={{ fontSize: 12, fontWeight: 500 }}>{row.caseQty}</span>
                                <span style={{ fontSize: 12, color: "#999" }}>{caseUnitLabelPrimaryReturn}</span>
                              </div>
                              <div style={{ width: "1px", height: 16, backgroundColor: "#E0E0E0" }} />
                            </>
                          )}
                          {otherUnitLabelPrimaryReturn && (
                            <>
                              <div style={{ display: "flex", alignItems: "center", gap: "2px", color: "rgb(124, 124, 124)" }}>
                                <span style={{ fontSize: 12, fontWeight: 500 }}>{row.otherQty}</span>
                                <span style={{ fontSize: 12, color: "#999" }}>{activeRow?.otherUnitName ?? otherUnitLabelPrimaryReturn}</span>
                              </div>
                              <div style={{ width: "1px", height: 16, backgroundColor: "#E0E0E0" }} />
                            </>
                          )}
                          {packUnitLabelPrimaryReturn && (
                            <div style={{ display: "flex", alignItems: "center", gap: "2px", color: "rgb(124, 124, 124)" }}>
                              <span style={{ fontSize: 12, fontWeight: 500 }}>{row.pieceQty}</span>
                              <span style={{ fontSize: 12, color: "#999" }}>{packUnitLabelPrimaryReturn}</span>
                            </div>
                          )}
                        </div>

                        <Box>
                          <Tooltip title={row.reason} arrow>
                            <Typography
                              fontSize="12px"
                              color="#666666"
                              fontWeight={500}
                              width="10rem"
                              sx={{
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                cursor: "default",
                                textAlign: "center",
                              }}
                            >
                              {row.reason}
                            </Typography>
                          </Tooltip>
                        </Box>
                      </Box>
                    );
                  });
                })()}
              </List>
            </Box>
          </Box>
        )}
      </Popover>
      <div className="view_order_header_with_search">
        <div className="view_order_header">
          <FormHeader
            parentLabel={"Primary Return"}
            label={"Create New Return"}
            goBackToParent={() => navigate(-1) }
          />
        </div>
        <div className="view_order_manage_order_search_bar">
          <SearchV2 searchFunction={handleSearchValueChange} />
        </div>
      </div>
      <div ref={scrollRef} style={{
            gap: "14px",
            overflow: "auto",
            height: "100%",
            scrollbarWidth: ConfigDictionary?.showTableScrollbar ? "thin" : "none",
          }}
          onScroll={(e) => {
            if (ConfigDictionary?.showTableScrollbar){
              const currentScrollTop = e.currentTarget.scrollTop;
              if (currentScrollTop > scrollTop) {
                setIsScrolling(true); // scrolling down
              } else if (currentScrollTop < scrollTop) {
                setIsScrolling(false); // scrolling up
              } 
            }
          }}  
      >
        <div style={{display: "flex", flexDirection: "column", gap: "10px", borderRadius: "10px", border: "1px solid #E7E7E7", padding: "10px"}}>
          <div>
            <CustomAccordianV2
              metaData1={EditPrimaryReturnMetadata(invoiceData)}
              data1={invoiceData}
              showMoreDetails={false}
            />
          </div>
        </div>
        <div style={{height: "100%", position: "sticky", top: "0"}}>
          <div className="view-primary-order-details">
            <div className="view-primary-order-details-header" style={{padding: "8px 0px"}}>
              <BorderHeading>Add Return Quantity</BorderHeading>
            </div>
            {loader ? (
              <Loader />
            ) : (
              <CustomTablewithTotalInfoV2
                data={filteredRows}
                columns={columns}
                totals={[]}
                allowTableScroll={allowTableScroll}
                expandableRowColumns={expandedRowColumns}
                setScrollTop={setScrollTop}
                headerBackgroundColor="#f3f9fb"
                handleClosePopover={handleClosePopover}
                children={
                  (
                      <button
                        className="create-return-btn"
                        style={{fontSize: "1.2em"}}
                        disabled={approvalButtonLoadingRef.current}
                        onClick={() => {handleClickRaiseButton()}}
                      >
                          {
                            approvalButtonLoading ?
                            <CircularProgress style={customStyle} /> :
                            <span style={{whiteSpace: "nowrap", fontWeight: 500}}>Raise Return</span>
                          }
                      </button>
                  )
                }
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditPrimaryReturn;
